/**
 * Cold-start run 8 asked for "a simple 'Outline -> Draft next scene' path".
 * One quiet line from the book's saved state (GET /next-step): how far along
 * it is, and the one next step. Suggested order: one sequence at a time, then
 * draft scene by scene. Never a gate; whole-book Generate stays where it is.
 */
import { outlineParts, readingOrderSceneIds } from './outlineCardRows';
import { draftThisScenePath } from './sceneWritingPath';
import {
  OUTLINE_NEXT_WRITE,
  PATH_ACCEPT_COUNTS,
  PATH_AFTER_THIS,
  PATH_ALL_DRAFTED,
  PATH_DRAFT_NEXT,
  PATH_EARLIER_SCENE,
  PATH_NEXT_SCENE,
  PATH_PROGRESS,
  PATH_REVIEW_NEXT,
  PATH_TALK_NEXT,
} from './writerCopy';

/** A scene still needs a draft, or its latest draft is not accepted. */
export function sceneStillOpen(scene) {
  if (!scene) return false;
  if (scene.accepted === true) return false;
  if (scene.unaccepted_draft) return true;
  if (scene.accepted === false) return true;
  return !scene.drafted;
}

/** True only when every scene's latest pass is accepted. */
export function allScenesAccepted(path) {
  const total = path?.scenes_total || 0;
  if (!total) return false;
  if ((path.scenes_unaccepted || 0) > 0) return false;
  if (path.scenes_accepted != null) return path.scenes_accepted === total;
  const scenes = path.scenes || [];
  if (scenes.length) {
    return scenes.every((scene) => {
      if (scene.unaccepted_draft) return false;
      if (scene.accepted === false) return false;
      if (scene.accepted === true) return true;
      return Boolean(scene.drafted);
    });
  }
  return path.next_scene == null && (path.scenes_drafted || 0) === total;
}

/** The next open scene after this one, in story order (wrapping round). */
export function nextUndraftedAfter(scenes, currentId) {
  const list = scenes || [];
  const here = currentId == null ? '' : String(currentId);
  const at = here
    ? list.findIndex((scene) => String(scene.id) === here)
    : -1;
  const after = at >= 0 ? [...list.slice(at + 1), ...list.slice(0, at)] : list;
  return after.find((scene) => sceneStillOpen(scene)) || null;
}

/**
 * The Outline header's main button. The scene it opens is the first one
 * still undrafted in outline-card order. When the path has no scene list,
 * a named next scene wins over the first column.
 */
export function outlineNextWrite(path, columns, structure = {}) {
  const list = columns || [];
  const known = Array.isArray(path?.scenes) ? path.scenes : null;
  const headers = structure?.headers;
  const frameworkNames = structure?.frameworkNames;
  const hasCardOrder = Boolean(
    (headers?.sequences && headers.sequences.length)
    || (headers?.acts && headers.acts.length)
    || (frameworkNames && frameworkNames.length)
  );
  if (known && hasCardOrder) {
    const flags = new Map(known.map((scene) => [String(scene.id), scene]));
    const ids = readingOrderSceneIds(outlineParts({
      columns: list,
      headers: headers || { acts: [], sequences: [] },
      frameworkNames: frameworkNames || [],
    }));
    for (const id of ids) {
      const scene = flags.get(id);
      if (scene && !sceneStillOpen(scene)) continue;
      const column = list.find((col) => String(col?.scene_id) === id) || null;
      if (!scene && !column) continue;
      const title = String(scene?.name || column?.display_name || '').trim();
      return {
        sceneId: id,
        title,
        column,
        label: PATH_NEXT_SCENE(title || 'Untitled scene'),
      };
    }
  }
  const named = path?.next_scene;
  const open = named && named.accepted !== true && named.id ? named : null;
  if (open) {
    const column = list.find((col) => String(col?.scene_id) === String(open.id)) || null;
    const title = String(open.name || column?.display_name || '').trim();
    return {
      sceneId: String(open.id),
      title,
      column,
      label: PATH_NEXT_SCENE(title || 'Untitled scene'),
    };
  }
  // Every known scene is accepted. The first column is not a scene left to draft.
  if (known && known.length && !known.some((scene) => sceneStillOpen(scene))) {
    return null;
  }
  const sceneColumns = list.filter((col) => col?.scene_id);
  const first = sceneColumns[0];
  if (!first) return null;
  return {
    sceneId: String(first.scene_id),
    title: String(first.display_name || '').trim(),
    column: first,
    label: OUTLINE_NEXT_WRITE(sceneColumns.indexOf(first) + 1),
  };
}

export function bookPathProgress(path) {
  if (!path) return '';
  const hasOutline = (path.scenes_total || 0) > 0 && path.sequences_with_scenes != null;
  const base = PATH_PROGRESS({
    approved: path.sequences_approved || 0,
    filled: path.sequences_with_scenes,
    sequences: path.has_framework ? path.sequences_total || 0 : 0,
    drafted: path.scenes_drafted || 0,
    scenes: path.scenes_total || 0,
    hasOutline,
  });
  const counts = PATH_ACCEPT_COUNTS(path.scenes_accepted || 0, path.scenes_unaccepted || 0);
  return counts && base ? `${base} ${counts}` : base;
}

/** Plot Workshop: same drafted / accepted / unaccepted counts as book_path. */
export function outlineProgressLine(progress) {
  if (!progress || !(progress.scenes_total > 0)) return '';
  const total = progress.sequences_total || 0;
  if (!total && !progress.scenes_total) return '';
  const drafted = progress.scenes_drafted != null
    ? progress.scenes_drafted
    : (progress.scenes_accepted || 0);
  const base = PATH_PROGRESS({
    approved: progress.sequences_with_scenes || 0,
    filled: progress.sequences_with_scenes,
    sequences: total,
    drafted,
    scenes: progress.scenes_total || 0,
    hasOutline: progress.sequences_with_scenes != null && total > 0,
  });
  const acceptedCount = Number(progress.scenes_accepted) || 0;
  if (progress.scenes_unaccepted == null && acceptedCount <= 0) return base;
  const counts = PATH_ACCEPT_COUNTS(acceptedCount, progress.scenes_unaccepted || 0);
  return counts && base ? `${base} ${counts}` : base;
}

/**
 * stage: 'plot' (Plot Focus, Plot Grid) or 'scene-writing'.
 * Returns { text, action, to?, sequence?, sceneId? } or null.
 */
export function bookPathTip(path, {
  stage,
  storyId,
  currentSceneId = '',
  excludeSequence = '',
  currentAccepted = false,
} = {}) {
  if (!path) return null;
  const text = bookPathProgress(path);
  const bookId = storyId || path.story_id;
  if (stage === 'scene-writing') {
    const next = nextUndraftedAfter(path.scenes, currentSceneId);
    if (!next) {
      if (path.scenes_total && allScenesAccepted(path)) {
        return { text: `${text} ${PATH_ALL_DRAFTED}`.trim() };
      }
      return text ? { text } : null;
    }
    const list = path.scenes || [];
    const at = list.findIndex((scene) => scene.id === currentSceneId);
    const earlier = at >= 0 && list.findIndex((scene) => scene.id === next.id) < at;
    // The Outline's next scene is the first one not accepted yet. While
    // that scene is the one on screen, the following open scene is after
    // this one. "Next scene" would name a different scene than the Outline.
    const here = at >= 0 ? list[at] : null;
    // Accept flips the pass on screen before the path refetch lands.
    // That pass is closed, so the following scene is the next one.
    const hereOpen = currentAccepted ? false : sceneStillOpen(here);
    const action = earlier
      ? PATH_EARLIER_SCENE(next.name)
      : (hereOpen ? PATH_AFTER_THIS(next.name) : PATH_NEXT_SCENE(next.name));
    return { text, action, sceneId: next.id };
  }
  const sequence = path.next_sequence && path.next_sequence !== excludeSequence
    ? path.next_sequence
    : null;
  if (sequence) return { text, action: PATH_TALK_NEXT(sequence), sequence };
  if (path.next_scene) {
    const name = path.next_scene.name;
    return {
      text,
      action: path.next_scene.unaccepted_draft ? PATH_REVIEW_NEXT(name) : PATH_DRAFT_NEXT(name),
      to: draftThisScenePath(bookId, { sceneId: path.next_scene.id, title: name }),
    };
  }
  if (path.scenes_total && allScenesAccepted(path)) {
    return { text: `${text} ${PATH_ALL_DRAFTED}`.trim() };
  }
  return text ? { text } : null;
}
